import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { type GHLDashboardStats, formatCost, calculateTotalCost, calculateWhisperCost, formatTokenCount } from '@/lib/ghl-dashboard'
import { BarChart3, CheckCircle, Cpu, Mic, TrendingUp } from 'lucide-react'

interface Props {
  readonly stats: GHLDashboardStats | null | undefined
  readonly isLoading: boolean
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
}: {
  title: string
  value: string
  subtitle?: string
  icon: React.ComponentType<{ className?: string }>
  isLoading: boolean
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-7 w-24 mt-1" />
            ) : (
              <p className="text-2xl font-bold">{value}</p>
            )}
            {subtitle && !isLoading && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <Icon className="h-8 w-8 text-muted-foreground/50" />
        </div>
      </CardContent>
    </Card>
  )
}

export function AnalyticsOverview({ stats, isLoading }: Props) {
  const claudeCost = stats
    ? calculateTotalCost(stats.total_input_tokens, stats.total_output_tokens)
    : 0

  const whisperCost = stats
    ? calculateWhisperCost(stats.total_whisper_duration ?? 0)
    : 0

  const totalCost = claudeCost + whisperCost

  const bookingRate = stats && stats.total_analyses > 0
    ? Math.round((stats.bookings_detected / stats.total_analyses) * 100)
    : 0

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      <StatCard
        title="Total Analyses"
        value={stats?.total_analyses.toLocaleString() ?? '0'}
        subtitle={`${formatTokenCount(stats?.total_input_tokens ?? 0)} input tokens`}
        icon={BarChart3}
        isLoading={isLoading}
      />
      <StatCard
        title="Bookings Detected"
        value={`${stats?.bookings_detected ?? 0}`}
        subtitle={`${bookingRate}% detection rate`}
        icon={CheckCircle}
        isLoading={isLoading}
      />
      <StatCard
        title="Claude Cost"
        value={formatCost(claudeCost)}
        subtitle={`${formatTokenCount((stats?.total_input_tokens ?? 0) + (stats?.total_output_tokens ?? 0))} tokens`}
        icon={Cpu}
        isLoading={isLoading}
      />
      <StatCard
        title="Whisper Cost"
        value={formatCost(whisperCost)}
        subtitle={`${Math.round((stats?.total_whisper_duration ?? 0) / 60)} min transcribed`}
        icon={Mic}
        isLoading={isLoading}
      />
      <StatCard
        title="Avg Confidence"
        value={`${stats?.avg_confidence ?? 0}%`}
        subtitle={`Total AI: ${formatCost(totalCost)}`}
        icon={TrendingUp}
        isLoading={isLoading}
      />
    </div>
  )
}
